import { RequestStatusEnum } from "@/lib/enum/request-status-enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IsFormable } from "@/lib/formable";
import { LeadGoToHelper } from "@/lib/utility/lead.go-to-helper";
import leadService from "@/services/mrfiktiv/services/lead.service";
import leadResponseService from "@/services/mrfiktiv/services/lead-response.service";
import {
  MrfiktivLeadPopulatedViewModelGen,
  MrfiktivLeadViewModelGen,
  MrfiktivPartnerCustomerUserViewModelGen,
  MrfiktivStatusHistoryElementViewModelGen,
  MrfiktivUpdateLeadDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { LeadDataAccessLayer } from "@/store/modules/access-layers/lead.access-layer";
import { PartnerUserAccessLayer } from "@/store/modules/access-layers/partner-user.access-layer";
import { RepairEstimateDataAccessLayer } from "@/store/modules/access-layers/repair-estimate.access-layer";
import { ReportDataAccessLayer } from "@/store/modules/access-layers/report.access-layer";
import { SignDocumentDataAccessLayer } from "@/store/modules/access-layers/sign-document.access-layer";
import { VehicleAccessLayer } from "@/store/modules/access-layers/vehicle.access-layer";
import { VideoDataAccessLayer } from "@/store/modules/access-layers/video.access-layer";
import VueRouter from "vue-router";
import { IRepairEstimate, RepairEstimate } from "./repair-estimate.entity";
import { IReport, Report } from "./report.entity";
import { ISignDocument, SignDocument } from "./sign-document.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { IPartnerUser, PartnerUser } from "./user.entity";
import { IVehicle, Vehicle } from "./vehicle.entity";
import { IVideo, Video } from "./video.entity";
import { ICreateDto } from "@/lib/utility/data/create-dto.interface";
import { $t } from "@/lib/utility/t";
import { ActivityLogModule } from "@/store/modules/activity-log.store";
import { PageFilterElement } from "./page-filter-element.entity";
import { PageFilterOperationEnum } from "@/lib/utility/data/page-filter-operation.enum";
import { CustomerAccountDataAccessLayer } from "@/store/modules/access-layers/customer-account.access-layer";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { ActivityLog } from "./activity-log.entity";
import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { PersonDataAccessLayer } from "@/store/modules/access-layers/person.access-layer";
import { IPerson, Person } from "./person.entity";
import { PartnerImageDataAccessLayer } from "@/store/modules/access-layers/partner-image.access-layer";
import { IPartnerImage, PartnerImage } from "./partner-image.entity";

@IsFormable
@IsFilterable
export class LeadBase implements MrfiktivLeadPopulatedViewModelGen, ICreateDto<ILead> {
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "id"
  })
  id!: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.lead.partnerId"
  })
  partnerId!: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.lead.userId"
  })
  userId!: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.lead.reportId"
  })
  reportId?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.lead.assignees"
  })
  assignees?: string[];

  @FilterConfig({
    type: Timestamp
  })
  timestamp!: ITimestamp;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.lead.title"
  })
  title?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.lead.description"
  })
  description?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.lead.externalId"
  })
  externalId?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.lead.email"
  })
  email?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.lead.phone"
  })
  phone?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.lead.firstName"
  })
  firstName?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.lead.lastName"
  })
  lastName?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.lead.numberplate"
  })
  numberplate?: string;

  videoIds?: string[];

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.lead.vehicleId"
  })
  vehicleId?: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.lead.repairEstimateId"
  })
  repairEstimateId?: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.lead.documentId"
  })
  documentId?: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.lead.customerId"
  })
  customerId?: string;

  imageIds?: string[];

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.lead.personId"
  })
  personId?: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.lead.status"
  })
  status!: RequestStatusEnum;

  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.lead.acceptedAt"
  })
  acceptedAt?: string;

  history!: MrfiktivStatusHistoryElementViewModelGen[];

  get user(): IPartnerUser | undefined {
    return PartnerUserAccessLayer.get(this.userId);
  }

  get report(): IReport | undefined {
    if (!this.reportId) return undefined;
    return ReportDataAccessLayer.get(this.reportId);
  }

  get videos(): IVideo[] | undefined {
    if (!this.videoIds?.length) return undefined;
    return this.videoIds.map(id => VideoDataAccessLayer.get(id)).filter((video): video is IVideo => !!video);
  }

  get vehicle(): IVehicle | undefined {
    if (!this.vehicleId) return undefined;
    return VehicleAccessLayer.get(this.vehicleId);
  }

  get repairEstimate(): IRepairEstimate | undefined {
    if (!this.repairEstimateId) return undefined;
    return RepairEstimateDataAccessLayer.get(this.repairEstimateId);
  }

  get document(): ISignDocument | undefined {
    if (!this.documentId) return undefined;
    return SignDocumentDataAccessLayer.get(this.documentId);
  }

  get customer(): MrfiktivPartnerCustomerUserViewModelGen | undefined {
    if (!this.customerId) return undefined;
    return CustomerAccountDataAccessLayer.get(this.customerId);
  }

  get person(): IPerson | undefined {
    if (!this.personId) return undefined;
    return PersonDataAccessLayer.get(this.personId);
  }

  get images(): IPartnerImage[] | undefined {
    if (!this.imageIds?.length) return undefined;
    return this.imageIds.map(id => PartnerImageDataAccessLayer.get(id)).filter((img): img is IPartnerImage => !!img);
  }

  get numberplateSuggestions() {
    const suggestions = new Set<string>();

    if (this.report?.numberplate) {
      suggestions.add(this.report.numberplate);
    }

    if (this.vehicle?.numberplate) {
      suggestions.add(this.vehicle.numberplate);
    }

    if (this.vehicle?.registration?.numberplate) {
      suggestions.add(this.vehicle.registration.numberplate);
    }

    if (this.repairEstimate?.report?.numberplate) {
      suggestions.add(this.repairEstimate.report.numberplate);
    }

    return Array.from(suggestions).filter(suggestion => suggestion !== this.numberplate);
  }

  get externalIdSuggestions() {
    const suggestions = new Set<string>();

    if (this.report?.externalId) {
      suggestions.add(this.report.externalId);
    }

    return Array.from(suggestions).filter(suggestion => suggestion !== this.externalId);
  }

  get firstNameSuggestions() {
    const suggestions = new Set<string>();

    if (this.report?.firstName) {
      suggestions.add(this.report.firstName);
    }

    if (this.person?.firstName) {
      suggestions.add(this.person.firstName);
    }

    if (this.customer?.firstName) {
      suggestions.add(this.customer.firstName);
    }

    return Array.from(suggestions).filter(suggestion => suggestion !== this.firstName);
  }

  get lastNameSuggestions() {
    const suggestions = new Set<string>();

    if (this.report?.lastName) {
      suggestions.add(this.report.lastName);
    }

    if (this.person?.lastName) {
      suggestions.add(this.person.lastName);
    }

    if (this.customer?.lastName) {
      suggestions.add(this.customer.lastName);
    }

    return Array.from(suggestions).filter(suggestion => suggestion !== this.lastName);
  }

  get emailSuggestions() {
    const suggestions = new Set<string>();

    if (this.report?.customerContact?.email) {
      suggestions.add(this.report.customerContact.email);
    }

    for (const email of this.person?.emails ?? []) {
      suggestions.add(email.email);
    }

    if (this.customer?.contact?.email) {
      suggestions.add(this.customer.contact.email);
    }

    return Array.from(suggestions).filter(suggestion => suggestion !== this.email);
  }

  get phoneSuggestions() {
    const suggestions = new Set<string>();

    if (this.report?.customerContact?.phone) {
      suggestions.add(this.report.customerContact.phone);
    }

    for (const phone of this.person?.phones ?? []) {
      suggestions.add(phone.phone);
    }

    if (this.customer?.contact?.phone) {
      suggestions.add(this.customer.contact.phone);
    }

    return Array.from(suggestions).filter(suggestion => suggestion !== this.phone);
  }

  get isUpdateable() {
    return this.status === RequestStatusEnum.DRAFT;
  }

  /**
   * Colors - based on @see SIGN_COLOR_BY_STATUS
   */
  static get statusColorMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "grey"],
      [RequestStatusEnum.REQUESTED, "primary"],
      [RequestStatusEnum.REPLIED, "green lighten-2"],
      [RequestStatusEnum.ACCEPTED, "green"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "red"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "red"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "orange"],
      [RequestStatusEnum.FINISHED, "deep-purple lighten-1"]
    ]);
  }

  static get statusIconMap() {
    return new Map([
      [RequestStatusEnum.DRAFT, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUESTED, "mdi-file-send-outline"],
      [RequestStatusEnum.REPLIED, "mdi-file-document-outline"],
      [RequestStatusEnum.REQUEST_MORE_DATA, "mdi-message-question-outline"],
      [RequestStatusEnum.ACCEPTED, "mdi-check-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTED, "mdi-close-circle-outline"],
      [RequestStatusEnum.REJECTED_BY_REQUESTING, "mdi-close-circle-outline"],
      [RequestStatusEnum.FINISHED, "mdi-check-bold"]
    ]);
  }

  /**
   * Colors - based on @see SIGN_COLOR_BY_STATUS
   */
  get statusColor() {
    return LeadBase.statusColorMap.get(this.status);
  }

  /**
   * Icon - based on @see SIGN_COLOR_BY_STATUS
   */
  get statusIcon() {
    return LeadBase.statusIconMap.get(this.status);
  }

  get statusReadable() {
    return $t(`enums.RequestStatusEnum.${this.status}`);
  }

  get isFinal(): boolean {
    const finals = [
      RequestStatusEnum.ACCEPTED,
      RequestStatusEnum.REJECTED_BY_REQUESTED,
      RequestStatusEnum.REJECTED_BY_REQUESTING,
      RequestStatusEnum.FINISHED
    ];

    return finals.includes(this.status);
  }

  get hasAttachments(): boolean {
    return !!((this.images && this.images.length) || (this.videos && this.videos.length));
  }

  get canUpdate() {
    return [RequestStatusEnum.DRAFT].includes(this.status);
  }

  constructor(data?: Partial<MrfiktivLeadViewModelGen | MrfiktivLeadPopulatedViewModelGen>) {
    if (!data) {
      return;
    }
    this.id = data.id ?? "";
    this.partnerId = data.partnerId ?? "";
    this.userId = data.userId ?? "";
    this.reportId = data.reportId;
    this.assignees = data.assignees;
    this.timestamp = new Timestamp(data.timestamp);
    this.title = data.title;
    this.description = data.description;
    this.externalId = data.externalId;
    this.numberplate = data.numberplate;
    this.email = data.email;
    this.phone = data.phone;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.videoIds = data.videoIds;
    this.vehicleId = data.vehicleId;
    this.repairEstimateId = data.repairEstimateId;
    this.documentId = data.documentId;
    this.customerId = data.customerId;
    this.status = (data.status as RequestStatusEnum) ?? RequestStatusEnum.DRAFT;
    this.history = data.history ?? [];
    this.acceptedAt = data.acceptedAt;
    this.imageIds = data.imageIds;
    this.personId = data.personId;
    this.mapPopulated(data as MrfiktivLeadPopulatedViewModelGen);
  }

  map(data: MrfiktivLeadViewModelGen | MrfiktivLeadPopulatedViewModelGen): void {
    this.id = data.id;
    this.partnerId = data.partnerId;
    this.userId = data.userId;
    this.reportId = data.reportId;
    this.assignees = data.assignees;
    this.timestamp = new Timestamp(data.timestamp);
    this.title = data.title;
    this.description = data.description;
    this.externalId = data.externalId;
    this.numberplate = data.numberplate;
    this.email = data.email;
    this.phone = data.phone;
    this.firstName = data.firstName;
    this.lastName = data.lastName;
    this.videoIds = data.videoIds;
    this.vehicleId = data.vehicleId;
    this.repairEstimateId = data.repairEstimateId;
    this.documentId = data.documentId;
    this.customerId = data.customerId;
    this.status = (data.status as RequestStatusEnum) ?? RequestStatusEnum.DRAFT;
    this.history = data.history ?? [];
    this.acceptedAt = data.acceptedAt;
    this.imageIds = data.imageIds;
    this.personId = data.personId;

    const populatedData = data as MrfiktivLeadPopulatedViewModelGen;
    this.mapPopulated(populatedData);
  }

  mapPopulated(populatedData: MrfiktivLeadPopulatedViewModelGen): void {
    const user = populatedData.user;
    if (user) {
      PartnerUserAccessLayer.set(new PartnerUser(user));
    }
    const report = populatedData.report;
    if (report) {
      ReportDataAccessLayer.set(new Report(report));
    }
    const videos = populatedData.videos;
    if (videos) {
      videos.forEach(video => VideoDataAccessLayer.set(new Video(video)));
    }
    const vehicle = populatedData.vehicle;
    if (vehicle) {
      VehicleAccessLayer.set(new Vehicle(vehicle));
    }
    const repairEstimate = populatedData.repairEstimate;
    if (repairEstimate) {
      RepairEstimateDataAccessLayer.set(new RepairEstimate(repairEstimate));
    }
    const document = populatedData.document;
    if (document) {
      SignDocumentDataAccessLayer.set(new SignDocument(document));
    }
    const customer = populatedData.customer;
    if (customer) {
      CustomerAccountDataAccessLayer.set(customer);
    }
    const images = populatedData.images;
    if (images) {
      images.forEach(image => PartnerImageDataAccessLayer.set(new PartnerImage(image)));
    }
    const person = populatedData.person;
    if (person) {
      PersonDataAccessLayer.set(new Person(person));
    }
  }

  goTo(router: VueRouter) {
    return {
      detail: () =>
        new LeadGoToHelper(router).goToLeadDetail({
          leadId: this.id,
          partnerId: this.partnerId
        }),
      table: () =>
        new LeadGoToHelper(router).goToLeadTable({
          partnerId: this.partnerId
        })
    };
  }

  async delete() {
    return leadService.delete(this.partnerId, this.id);
  }

  async update() {
    return this.updatePartial({
      reportId: this.reportId,
      assignees: this.assignees,
      title: this.title,
      description: this.description,
      externalId: this.externalId,
      numberplate: this.numberplate,
      email: this.email,
      phone: this.phone,
      firstName: this.firstName,
      lastName: this.lastName,
      videoIds: this.videoIds,
      vehicleId: this.vehicleId,
      repairEstimateId: this.repairEstimateId,
      documentId: this.documentId,
      customerId: this.customerId,
      imageIds: this.imageIds,
      personId: this.personId,
      status: this.status
    });
  }

  async updatePartial(dto: MrfiktivUpdateLeadDtoGen) {
    const updated = await leadService.update(this.partnerId, this.id, dto);

    this.map(updated);
    LeadDataAccessLayer.set(this);

    // there might be new activity logs related to this lead, so refresh activity log store
    ActivityLogModule.setFilters([
      new PageFilterElement({
        key: "source.refId",
        value: this.id,
        operation: PageFilterOperationEnum.EQUAL
      })
    ]);
    ActivityLogModule.fetchFirstPage({
      partnerId: this.partnerId,
      itemsPerPage: 5
    });

    return this;
  }

  async create() {
    const created = await leadService.create(this.partnerId, {
      reportId: this.reportId,
      assignees: this.assignees,
      title: this.title,
      description: this.description,
      externalId: this.externalId,
      numberplate: this.numberplate,
      email: this.email,
      phone: this.phone,
      firstName: this.firstName,
      lastName: this.lastName,
      videoIds: this.videoIds,
      vehicleId: this.vehicleId,
      repairEstimateId: this.repairEstimateId,
      documentId: this.documentId,
      customerId: this.customerId,
      imageIds: this.imageIds,
      personId: this.personId,
      status: this.status
    });

    this.map(created);
    LeadDataAccessLayer.set(this);

    return this;
  }

  async fetch() {
    const res = await leadService.getOne(this.partnerId, this.id);
    this.map(res);
    LeadDataAccessLayer.set(this);

    return this;
  }

  /**
   * Respond to a lead (accept/reject) via the lead response API
   */
  async respond(
    status: RequestStatusEnum.ACCEPTED | RequestStatusEnum.REJECTED_BY_REQUESTED | RequestStatusEnum.REQUEST_MORE_DATA,
    message: string,
    sct: string
  ) {
    const updated = await leadResponseService.respond(
      {
        partnerId: this.partnerId,
        leadId: this.id,
        sct: sct
      },
      {
        status: status,
        message: message
      }
    );

    this.map(updated);
    LeadDataAccessLayer.set(this);

    return this;
  }

  async updateStatusLog() {
    await new ActivityLog().updateStatusLog({
      state: this.status,
      source: { refId: this.id, refType: ResourceEnum.LEAD },
      partnerId: this.partnerId,
      comment: $t(`enums.RequestStatusEnum.${this.status}`)
    });

    return this;
  }

  async createAssigneeActivity(
    activityType: ActivityTypeEnum.CREATE_ASSIGNEE | ActivityTypeEnum.DELETE_ASSIGNEE,
    newAssignees?: string[]
  ) {
    if (!newAssignees?.length) return;

    await new ActivityLog().createAssigneeActivity({
      partnerId: this.partnerId,
      source: {
        refType: ResourceEnum.LEAD,
        refId: this.id
      },
      newAssignees,
      activityType
    });
  }
}

type ILead = LeadBase;
const Lead = Filter.createForClass(LeadBase);
export { ILead, Lead };
